import core from 'core';
import actions from 'actions';
import selectors from 'selectors';

/**
 * @typedef {Object} UI.IssueManager.Creator
 * @property {number} id The creator's ID
 * @property {string} full_name The creator's full name
 * @property {string} role The creator's role
 */

/**
 * @typedef {Object} UI.IssueManager.Issue
 * @property {number} id The issue ID
 * @property {number} document_id The document ID
 * @property {number} project_id The project ID
 * @property {number} priority_id The priority ID
 * @property {number} discipline_id The discipline ID
 * @property {number} status_id The status ID
 * @property {number} [creator_id] The creator ID (optional)
 * @property {number} [assignee_id] The assignee ID (optional)
 * @property {string} summary The issue summary
 * @property {string} full_text The full issue text
 * @property {Object} [assignee] The assignee details (optional)
 * @property {Object} [creator] The creator details (optional)
 * @property {boolean} [is_favourite] Whether the issue is marked as favorite (optional)
 * @property {Array} comments The issue comments
 * @property {string} created_at The creation timestamp
 * @property {string} updated_at The last update timestamp
 * @property {string} xfdf The XFDF data of the annotation
 */

/**
 * @class
 * @name IssueManager
 * @memberof UI
 * @extends Core.EventHandler
 */
class IssueManager {
  initialize(store, annotManager) {
    this.store = store;
    /**
     * the key represents the event name
     * the value is an array of callbacks that will be called when the given key event is triggered
     * @type {Object.<string, Function[]>}
     * @ignore
     */
    this.events = {};

    // Listen for annotation changes from the core
    // annotManager.addEventListener('annotationChanged', (annotations, action) => {
    //   if (!annotations.length) {
    //     return;
    //   }

    //   if (action === 'add') {
    //     this.handleAnnotationsAdded(annotations);
    //   } else if (action === 'modify') {
    //     this.handleAnnotationsModified(annotations);
    //   } else if (action === 'delete') {
    //     this.handleAnnotationsDeleted(annotations);
    //   }
    // });
  }

  /**
   * Sets the LT annotations that will be displayed in the LT annotations overlay
   * @method UI.IssueManager#setIssues
   * @param {UI.IssueManager.Annotation[]} annotations An array of LT annotations
   */
  setIssues(annotations) {
    this.store.dispatch(actions.setIssues(annotations));
  }

  /**
   * Updates a specific issue with new properties
   * @method UI.IssueManager#updateIssue
   * @param {number} issueId The ID of the issue to update
   * @param {Object} updates The properties to update on the issue
   */
  updateIssue(issueId, updates) {
    this.store.dispatch(actions.updateIssue(issueId, updates));
    this.trigger('issueUpdate', this.getIssues(), 'modify', { issueId });
  }

  /**
   * Gets the LT annotations
   * @method UI.IssueManager#getIssues
   * @returns {UI.IssueManager.Annotation[]} An array of LT annotations
   */
  getIssues() {
    return selectors.getIssues(this.store.getState());
  }

  /**
   * Checks if an annotation is attached to an issue
   * @method UI.IssueManager#isAnnotationAttachedToIssue
   * @param {UI.IssueManager.Annotation} annotation The annotation to check
   * @returns {boolean} True if the annotation is attached to an issue, false otherwise
   */
  isAnnotationAttachedToIssue(annotation) {
    const issues = this.getIssues();

    const issue = issues.find(issue => issue.id.toString() === annotation.Id);
    if (!issue) {
      return false;
    }

    return true;
  }

  shouldShowInPanel(annotation, selectedAnnotations) {
    // only show the selected issue in panel
    const isIssueAttached = selectedAnnotations.some(a => a.Id === annotation.Id);
    return isIssueAttached;
  }

  handleAnnotationsAdded(annotations) {
    const newAnnotations = [];

    if (newAnnotations.length) {
      this.trigger('issueUpdate', [newAnnotations, 'add']);
    }
  }

  handleAnnotationsModified(annotations) {
    const modifiedAnnotations = [];

    annotations.forEach(annotation => {
      // TODO: fix this
      const annotationData = this.createAnnotationData(annotation);
      modifiedAnnotations.push(annotationData);
    });

    if (modifiedAnnotations.length) {
      this.trigger('issueUpdate', [modifiedAnnotations, 'modify']);
    }
  }

  handleAnnotationsDeleted(annotations) {
    const deletedAnnotations = [];

    if (deletedAnnotations.length) {
      this.trigger('issueUpdate', [deletedAnnotations, 'delete']);
    }
  }

  on(eventName, fn) {
    if (!this.events[eventName]) {
      this.events[eventName] = [];
    }

    this.events[eventName].push(fn);
    return this;
  }

  off(eventName, fn) {
    if (!this.events) {
      return this;
    }

    if (typeof eventName === 'undefined') {
      this.events = {};
    }

    if (!this.events[eventName]) {
      return this;
    }

    if (fn) {
      this.events[eventName] = this.events[eventName].filter(handler => handler !== fn);
    } else {
      this.events[eventName] = [];
    }

    return this;
  }

  one(eventName, fn) {
    const origFn = fn;

    fn = (...args) => {
      this.off(eventName, fn);
      origFn.apply(this, args);
    };

    return this.on(eventName, fn);
  }

  trigger(eventName, ...data) {
    if (!this.events) {
      return this;
    }

    if (!this.events[eventName]) {
      return this;
    }

    // normalize data if only an array is passed in as data
    // for example: trigger('issueUpdate', [annotations])
    // data will be passed to the handler: (annotations) => {}
    if (data.length === 1 && Array.isArray(data[0])) {
      data = data[0];
    }

    this.events[eventName].forEach(fn => {
      fn.call(this, ...data);
    });

    return this;
  }

  addEventListener(...args) {
    this.on(...args);
  }

  removeEventListener(...args) {
    this.off(...args);
  }
}

/**
 * Triggered when issues have been updated (added, deleted, modified).
 * Attach like instance.issueManager.on('issueUpdate', callback)
 * @name UI.IssueManager#issueUpdate
 * @event
 * @param {UI.IssueManager.Issue[]} issues The issues that were updated
 * @param {'add'|'modify'|'delete'} action The action that occurred (add, delete, modify)
 * @param {Object} [options] Additional options
 */

export default new IssueManager();
export { IssueManager };
